import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { Container, Row, Col } from 'react-bootstrap'
import { fetchQuiz, submitQuiz } from '../actions/courseActions'
import { QUIZ_SUBMIT_RESET } from '../constants/courseConstants'
import Loader from '../components/Loader'
import Message from '../components/Message'

const LETTER_COLORS = { A: '#4361ee', B: '#7209b7', C: '#f72585', D: '#4cc9f0' }

// ─── Single question card ─────────────────────────────────────────────────────
function QuestionCard({ question, index, total, selected, onSelect, result }) {
    const choices = [
        { key: 'A', text: question.choice_a },
        { key: 'B', text: question.choice_b },
        { key: 'C', text: question.choice_c },
        { key: 'D', text: question.choice_d },
    ]

    const getChoiceStyle = (key) => {
        const base = {
            display: 'flex', alignItems: 'flex-start', gap: 12,
            padding: '0.85rem 1.1rem', borderRadius: 10, marginBottom: 10,
            cursor: result ? 'default' : 'pointer',
            border: '2px solid transparent',
            transition: 'all 0.18s ease',
            fontWeight: 500, fontSize: '0.95rem',
        }
        if (result) {
            if (key === result.correct_answer) return { ...base, background: '#d4edda', border: '2px solid #28a745', color: '#155724' }
            if (key === selected && key !== result.correct_answer) return { ...base, background: '#f8d7da', border: '2px solid #dc3545', color: '#721c24' }
            return { ...base, background: '#f8f9fa', color: '#6c757d' }
        }
        if (selected === key) return { ...base, background: 'var(--navy)', color: 'var(--white)', border: '2px solid var(--navy)' }
        return { ...base, background: '#f4f6fb', border: '2px solid #e9ecef', ':hover': { borderColor: 'var(--gold)' } }
    }

    return (
        <div style={{
            background: 'var(--white)', borderRadius: 'var(--radius)',
            boxShadow: 'var(--shadow)', padding: '1.75rem 2rem', marginBottom: '1.5rem',
            animation: 'fadeIn 0.3s ease',
        }}>
            {/* Question number + text */}
            <div style={{ display: 'flex', gap: 14, marginBottom: '1.25rem', alignItems: 'flex-start' }}>
                <div style={{
                    minWidth: 36, height: 36, borderRadius: '50%',
                    background: 'var(--navy)', color: 'var(--gold)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, fontSize: '0.9rem', flexShrink: 0,
                }}>
                    {index + 1}
                </div>
                <p style={{ margin: 0, fontWeight: 600, lineHeight: 1.55, fontSize: '1rem' }}>
                    {question.text}
                </p>
            </div>

            {/* Choices */}
            {choices.map(({ key, text }) => (
                <div key={key} style={getChoiceStyle(key)} onClick={() => !result && onSelect(key)}>
                    <span style={{
                        minWidth: 28, height: 28, borderRadius: '50%',
                        background: LETTER_COLORS[key], color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 700, fontSize: '0.8rem', flexShrink: 0,
                    }}>
                        {key}
                    </span>
                    <span>{text}</span>
                    {result && key === result.correct_answer && (
                        <i className="fa-solid fa-circle-check ms-auto" style={{ color: '#28a745', marginTop: 3 }}></i>
                    )}
                    {result && key === selected && key !== result.correct_answer && (
                        <i className="fa-solid fa-circle-xmark ms-auto" style={{ color: '#dc3545', marginTop: 3 }}></i>
                    )}
                </div>
            ))}

            {/* Explanation (shown after grading) */}
            {result && (
                <div style={{
                    background: '#fff8e1', borderLeft: '4px solid var(--gold)',
                    borderRadius: 8, padding: '0.85rem 1rem', marginTop: '0.75rem',
                    fontSize: '0.88rem', color: '#5a4000',
                }}>
                    <i className="fa-solid fa-lightbulb me-2" style={{ color: 'var(--gold)' }}></i>
                    <strong>Explanation:</strong> {result.explanation}
                </div>
            )}
        </div>
    )
}

// ─── Results summary banner ───────────────────────────────────────────────────
function ResultsBanner({ score, correct, total, sectionId, navigate }) {
    const passed = score >= 72
    return (
        <div style={{
            background: passed ? 'linear-gradient(135deg,#1a472a,#2d6a4f)' : 'linear-gradient(135deg,var(--navy),#2c3e6b)',
            borderRadius: 'var(--radius)', padding: '2rem', marginBottom: '2rem',
            color: '#fff', textAlign: 'center', boxShadow: 'var(--shadow)',
        }}>
            <button
                onClick={() => window.location.reload()}
                title="Retake Quiz"
                style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    display: 'block', margin: '0 0 0.5rem 0',
                    transition: 'transform 0.2s ease',
                }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1) rotate(15deg)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1) rotate(0deg)'}
            >
                <i className={`fa-solid fa-${passed ? 'trophy' : 'rotate'} fa-3x`}
                   style={{ color: passed ? '#ffd700' : 'var(--gold-lt)' }}></i>
            </button>
            <h2 style={{ fontWeight: 800, color: passed ? '#ffd700' : 'var(--gold-lt)' }}>
                {passed ? 'Great Work!' : 'Keep Studying!'}
            </h2>
            <p style={{ fontSize: '1.1rem', opacity: 0.9 }}>
                You scored <strong style={{ fontSize: '1.4rem' }}>{score}%</strong> — {correct} of {total} correct
            </p>
            <div style={{ margin: '1rem auto', maxWidth: 320 }}>
                <div style={{ height: 10, background: 'rgba(255,255,255,0.2)', borderRadius: 20 }}>
                    <div style={{
                        height: '100%', borderRadius: 20, transition: 'width 0.8s ease',
                        width: `${score}%`,
                        background: passed ? '#ffd700' : 'var(--gold-lt)',
                    }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', opacity: 0.7, marginTop: 4 }}>
                    <span>0%</span><span style={{ color: '#ffd700' }}>72% passing</span><span>100%</span>
                </div>
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: '1rem' }}>
                <button className="btn-gold" onClick={() => window.location.reload()}>
                    <i className="fa-solid fa-rotate me-2"></i>Retake Quiz
                </button>
                <button className="btn-navy" style={{ background: 'rgba(255,255,255,0.15)', color: '#fff' }}
                    onClick={() => navigate('/course')}>
                    <i className="fa-solid fa-graduation-cap me-2"></i>Back to Course
                </button>
            </div>
        </div>
    )
}

// ─── Main QuizScreen ──────────────────────────────────────────────────────────
function QuizScreen() {
    const dispatch  = useDispatch()
    const navigate  = useNavigate()
    const { sectionId } = useParams()

    const userLogin    = useSelector(state => state.userLogin)
    const { userInfo } = userLogin
    const { loading: quizLoading, error: quizError, quiz } = useSelector(state => state.quizFetch)
    const { loading: submitting, error: submitError, result } = useSelector(state => state.quizSubmit)

    const [answers, setAnswers] = useState({})        // { questionId: letter }
    const [tokens, setTokens]   = useState({})        // { questionId: choice_token }

    useEffect(() => {
        if (!userInfo) { navigate('/login'); return }
        dispatch({ type: QUIZ_SUBMIT_RESET })
        dispatch(fetchQuiz(sectionId))
    }, [dispatch, navigate, userInfo, sectionId])

    const handleSelect = (questionId, letter, token) => {
        if (result) return  // locked after submit
        setAnswers(prev => ({ ...prev, [questionId]: letter }))
        setTokens(prev  => ({ ...prev, [questionId]: token }))
    }

    const handleSubmit = () => {
        const answersArray = Object.entries(answers).map(([question_id, selected_answer]) => ({
            question_id: parseInt(question_id),
            selected_answer,
            choice_token: tokens[question_id] || '',
        }))
        dispatch(submitQuiz(sectionId, answersArray))
    }

    useEffect(() => {
        if (result) window.scrollTo({ top: 0, behavior: 'smooth' })
    }, [result])

    const answeredCount = Object.keys(answers).length
    const totalQuestions = quiz?.questions?.length || 0
    const allAnswered = answeredCount === totalQuestions && totalQuestions > 0

    // Build id-keyed lookup for graded questions (already in shuffled space from backend)
    const gradedMap = {}   // _id → graded question
    if (result) {
        result.questions?.forEach(q => { gradedMap[q._id] = q })
    }
    const getResult = (questionId) => {
        const graded = gradedMap[questionId]
        if (!graded) return null
        return {
            correct_answer: graded.correct_answer,        // shuffled letter
            explanation:    graded.explanation,
            selected:       result.shuffled_selected?.[questionId],  // shuffled letter user picked
            is_correct:     graded.is_correct,
        }
    }

    if (quizLoading) return <Container><Loader /></Container>
    if (quizError)   return <Container><Message variant="danger">{quizError}</Message></Container>
    if (!quiz)       return null

    return (
        <Container>
            {/* Header */}
            <div style={{ marginBottom: '1.5rem' }}>
                <Link to={`/section/${sectionId}`} style={{ color: 'var(--muted)', textDecoration: 'none', fontSize: '0.85rem' }}>
                    <i className="fa-solid fa-arrow-left me-1"></i>Back to Study Material
                </Link>
                <h2 style={{ fontWeight: 800, color: 'var(--navy)', marginTop: '0.5rem' }}>
                    <i className="fa-solid fa-pen-to-square me-2" style={{ color: 'var(--gold)' }}></i>
                    {quiz.title}
                </h2>
                <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>
                    {totalQuestions} questions — 72% required to pass
                </p>
            </div>

            {/* Results banner */}
            {result && (
                <ResultsBanner
                    score={result.score}
                    correct={result.correct}
                    total={result.total}
                    sectionId={sectionId}
                    navigate={navigate}
                />
            )}

            {/* Progress bar while answering */}
            {!result && (
                <div style={{
                    position: 'sticky', top: '10px', zIndex: 100,
                    background: 'var(--white)', borderRadius: 'var(--radius)',
                    boxShadow: 'var(--shadow)', padding: '1rem 1.5rem', marginBottom: '1.5rem',
                    display: 'flex', alignItems: 'center', gap: 16,
                }}>
                    <i className="fa-solid fa-list-check" style={{ color: 'var(--gold)', fontSize: '1.2rem' }}></i>
                    <div style={{ flex: 1 }}>
                        <div className="progress-label">
                            <span>Answered</span>
                            <span>{answeredCount} / {totalQuestions}</span>
                        </div>
                        <div className="progress">
                            <div className="progress-bar" style={{ width: `${(answeredCount / totalQuestions) * 100}%` }} />
                        </div>
                    </div>
                </div>
            )}

            {/* Questions */}
            {quiz.questions.map((q, i) => (
                <QuestionCard
                    key={q._id}
                    question={result && gradedMap[q._id] ? gradedMap[q._id] : q}
                    index={i}
                    total={totalQuestions}
                    selected={result ? result.shuffled_selected?.[q._id] : answers[q._id]}
                    onSelect={(letter) => handleSelect(q._id, letter, q.choice_token)}
                    result={result ? getResult(q._id) : null}
                />
            ))}

            {/* Submit */}
            {!result && (
                <div style={{ textAlign: 'center', padding: '1rem 0 2rem' }}>
                    {submitError && <Message variant="danger">{submitError}</Message>}
                    <button
                        className="btn-gold"
                        style={{ fontSize: '1.05rem', padding: '0.7rem 2.5rem', opacity: allAnswered ? 1 : 0.5, cursor: allAnswered ? 'pointer' : 'not-allowed' }}
                        disabled={!allAnswered || submitting}
                        onClick={handleSubmit}
                    >
                        {submitting
                            ? <><i className="fa-solid fa-spinner fa-spin me-2"></i>Grading…</>
                            : <><i className="fa-solid fa-paper-plane me-2"></i>Submit Quiz</>
                        }
                    </button>
                    {!allAnswered && (
                        <p style={{ marginTop: '0.5rem', color: 'var(--muted)', fontSize: '0.85rem' }}>
                            Answer all {totalQuestions} questions to submit
                        </p>
                    )}
                </div>
            )}

            <style>{`
                @keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
            `}</style>
        </Container>
    )
}

export default QuizScreen
